import { useEffect, useState } from 'react';
import './confirm.css';

/**
 * hub.360face.pt/confirm/<token> — the client confirms the visit from Hub's e-mail
 * (owner 22.10.2026). English and Portuguese together; the booking changes only on the button.
 */
export interface ConfirmView {
  start: string;
  services: string[];
  staffName?: string;
  studio: string;
  status: 'waiting' | 'confirmed' | 'arrived';
}

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; view: ConfirmView; busy: boolean }
  | { kind: 'done'; view: ConfirmView }
  | { kind: 'error'; code: string };

interface ApiBody {
  data?: unknown;
  error?: { code?: string };
}

const WHATSAPP = 'https://wa.me/351915311522';
const EN_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function tokenFromPath(path: string): string {
  return decodeURIComponent(path.replace(/^\/confirm\/?/, '').split('/')[0] ?? '');
}

function when(start: string): string {
  const [, m, d] = start.slice(0, 10).split('-').map(Number) as [number, number, number];
  return `${d} ${EN_MONTHS[m - 1]}, ${start.slice(11, 16)}`;
}

async function call(token: string, method: 'GET' | 'POST', f: typeof fetch): Promise<{ ok: boolean; body: ApiBody }> {
  const r = await f(`/v1/confirm/${encodeURIComponent(token)}`, { method });
  return { ok: r.ok, body: (await r.json().catch(() => ({}))) as ApiBody };
}

export function Confirm({ token, fetchImpl = fetch }: { token: string; fetchImpl?: typeof fetch }) {
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    if (!token) return setState({ kind: 'error', code: 'CONFIRM_LINK_INVALID' });
    call(token, 'GET', fetchImpl)
      .then(({ ok, body }) => {
        if (!ok) return setState({ kind: 'error', code: body?.error?.code ?? 'ERROR' });
        const view = body.data as ConfirmView;
        setState(view.status === 'waiting' ? { kind: 'ready', view, busy: false } : { kind: 'done', view });
      })
      .catch(() => setState({ kind: 'error', code: 'NETWORK' }));
  }, [token, fetchImpl]);

  const confirm = async () => {
    if (state.kind !== 'ready') return;
    setState({ ...state, busy: true });
    try {
      const { ok, body } = await call(token, 'POST', fetchImpl);
      if (ok) setState({ kind: 'done', view: state.view });
      else setState({ kind: 'error', code: body?.error?.code ?? 'ERROR' });
    } catch {
      setState({ kind: 'error', code: 'NETWORK' });
    }
  };

  return (
    <main className="cf">
      <header className="cf-band">
        <span className="cf-line">All You Need Is…</span>
        <strong className="cf-mark">
          360<i>°</i>FACE
        </strong>
      </header>
      <section className="cf-card">
        {state.kind === 'loading' && <p className="cf-mute">…</p>}
        {(state.kind === 'ready' || state.kind === 'done') && (
          <>
            {state.kind === 'done' ? (
              <>
                <h1>Thank you — see you soon!</h1>
                <p className="cf-pt">Obrigada — até breve!</p>
              </>
            ) : (
              <>
                <h1>Please confirm your visit</h1>
                <p className="cf-pt">Confirme a sua visita</p>
              </>
            )}
            <dl className="cf-details">
              <div>
                <dt>Date · Data</dt>
                <dd>{when(state.view.start)}</dd>
              </div>
              <div>
                <dt>Treatment · Tratamento</dt>
                <dd>{state.view.services.join(' + ')}</dd>
              </div>
              {state.view.staffName && (
                <div>
                  <dt>Therapist · Terapeuta</dt>
                  <dd>{state.view.staffName}</dd>
                </div>
              )}
              <div>
                <dt>Studio · Estúdio</dt>
                <dd>{state.view.studio}</dd>
              </div>
            </dl>
            {state.kind === 'ready' ? (
              <button className="cf-btn" onClick={confirm} disabled={state.busy}>
                Confirm visit · Confirmar
              </button>
            ) : (
              <p className="cf-ok" role="status">
                ✓ Your visit is confirmed · A sua visita está confirmada
              </p>
            )}
          </>
        )}
        {state.kind === 'error' && (
          <>
            <h1>
              {state.code === 'CONFIRM_LINK_EXPIRED' ? 'This link has expired' : 'We could not confirm right now'}
            </h1>
            <p className="cf-pt">
              {state.code === 'CONFIRM_LINK_EXPIRED' ? 'Este link já expirou' : 'Não foi possível confirmar agora'}
            </p>
          </>
        )}
        <p className="cf-help">
          Need to change it? · Precisa de alterar?{' '}
          <a href={WHATSAPP} rel="noopener noreferrer">
            WhatsApp +351 915 311 522
          </a>
        </p>
      </section>
    </main>
  );
}
